// @flow
import React from 'react';
import type { ChildrenArray, Element } from 'react';
import { types as t } from '../validation/runtime-checks/prop-spec.js';
import { Text } from '../Text.js';
import Latex from '../Latex.js';
import { List } from '../List.js';
import Image from '../Image.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { SolidifyAnnotatedFigure } from './SolidifyAnnotatedFigure.js';
import { SolidifyFigureGrid } from './SolidifyFigureGrid.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import { usePropsValidation } from '../validation/runtime-checks/index.js';
import { CollapsibleInternal } from '../CollapsibleInternal.js';
import { illustrationColor, productColor } from '../designSystem.js';
import {
  DisabilityIcon,
  EnglishLanguageLearnerIcon,
  InstructionalSupportInformationIcon,
  CommonMisconceptionIcon,
} from '../InstructionalSupportIcons.js';
import DraftImage from '../DraftImage.js';
import { LatexTextAtom } from '../LatexTextAtom.js';
import { SolidifyTechnologyToolScreenshot } from './SolidifyTechnologyToolScreenshot.js';

export function STNInstructionalSupport(props: {|
  children: ChildrenArray<
    | Element<typeof STNInstructionalSupportDisability>
    | Element<typeof STNInstructionalSupportInformation>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    STNInstructionalSupport,
  );

  if (failedValidation) return null;
  return (
    <div style={{ color: productColor.shuttleGrey, marginLeft: 30 }}>
      <h1
        style={{
          fontFamily: 'Proxima nova',
          fontStyle: 'normal',
          fontWeight: 600,
          fontSize: 18,
          color: productColor.cloudburst,
          lineHeight: '24px',
          marginBottom: 18,
          marginTop: 18,
        }}
      >
        Equitable instructional routines
      </h1>
      {props.children}
    </div>
  );
}
STNInstructionalSupport.spec = {
  children: t.minimumCount(
    1,
    t.unionOfTypes(
      t.elementType(STNInstructionalSupportDisability),
      t.elementType(STNInstructionalSupportInformation),
      t.elementType(STNEnglishLearnerSupport),
      t.elementType(STNCommonMisconceptions),
    ),
  ),
};

type InstructionSupportChildren = ChildrenArray<
  // Primitives
  | Element<typeof Text>
  | Element<typeof Latex>
  | Element<typeof LatexTextAtom>
  | Element<typeof List>
  | Element<typeof Image>
  // probably only need those above this line
  | Element<typeof BoxAndWhiskerPlot>
  | Element<typeof CartesianPlane>
  | Element<typeof NumberLine>
  | Element<typeof StemAndLeafPlot>
  | Element<typeof TabularData>
  | Element<typeof SolidifyAnnotatedFigure>
  | Element<typeof SolidifyFigureGrid>
  | Element<typeof TwoColumnProof>
  | Element<typeof AlgebraicWorking>
  // components designed for Solidify
  | Element<typeof SolidifyAnnotatedFigure>
  | Element<typeof SolidifyFigureGrid>
  | Element<typeof SolidifyTechnologyToolScreenshot>,
>;

// Each instructional support allows the same child components
const instructionSupportChildrenChecker = t.childrenArray(
  t.unionOfTypes(
    // Primitives
    t.elementType(Text),
    t.elementType(Latex),
    t.elementType(LatexTextAtom),
    t.elementType(List),
    t.elementType(Image),
    t.elementType(DraftImage),
    // again, probably only need those above this line
    t.elementType(BoxAndWhiskerPlot),
    t.elementType(CartesianPlane),
    t.elementType(NumberLine),
    t.elementType(StemAndLeafPlot),
    t.elementType(TabularData),
    t.elementType(TwoColumnProof),
    t.elementType(AlgebraicWorking),
    // components designed for Solidify
    t.elementType(SolidifyAnnotatedFigure),
    t.elementType(SolidifyFigureGrid),
    t.elementType(SolidifyTechnologyToolScreenshot),
  ),
);

const CollapseChevron = ({ isOpen }) => (
  <svg
    width="12"
    height="7.5"
    viewBox="0 0 8 5"
    fill="none"
    style={{ transform: isOpen ? `rotate(180deg)` : undefined }}
  >
    <path
      d="M3.60957 4.51196L0.649878 0.812348C0.387973 0.484966 0.621059 0 1.04031 0H6.95969C7.37894 0 7.61203 0.484966 7.35012 0.812348L4.39043 4.51196C4.19027 4.76216 3.80973 4.76216 3.60957 4.51196Z"
      fill={illustrationColor.cloudburst}
    />
  </svg>
);

export function STNInstructionalSupportDisability(props: {|
  children: InstructionSupportChildren,
  subheading: string,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    STNInstructionalSupportDisability,
  );

  if (failedValidation) return null;

  return (
    <div
      style={{
        backgroundColor: productColor.aliceBlue,
        padding: 20,
        paddingTop: 1,
        paddingBottom: 1,
        borderRadius: 8,
        marginBottom: 10,
      }}
    >
      <CollapsibleInternal
        openByDefault
        hideHorizontalRule
        title="Support students with disabilities"
        icon={<DisabilityIcon />}
        customChevron={CollapseChevron}
      >
        <p
          style={{
            fontWeight: 600,
            color: illustrationColor.regentGray,
            fontSize: 16,
            lineHeight: '22px',
            marginBottom: 16,
          }}
        >
          {props.subheading}
        </p>
        {props.children}
      </CollapsibleInternal>
    </div>
  );
}
STNInstructionalSupportDisability.spec = {
  children: instructionSupportChildrenChecker,
  subheading: t.string,
};

export function STNInstructionalSupportInformation(props: {|
  children: InstructionSupportChildren,
  subheading: string,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    STNInstructionalSupportInformation,
  );

  if (failedValidation) return null;

  return (
    <div
      style={{
        backgroundColor: productColor.aliceBlue,
        padding: 20,
        paddingTop: 1,
        paddingBottom: 1,
        borderRadius: 8,
        marginBottom: 10,
      }}
    >
      <CollapsibleInternal
        hideHorizontalRule
        openByDefault
        title="Targeted instructional strategies"
        icon={<InstructionalSupportInformationIcon />}
        customChevron={CollapseChevron}
      >
        <p
          style={{
            fontWeight: 600,
            color: illustrationColor.regentGray,
            fontSize: 16,
            lineHeight: '22px',
            marginBottom: 16,
          }}
        >
          {props.subheading}
        </p>
        {props.children}
      </CollapsibleInternal>
    </div>
  );
}
STNInstructionalSupportInformation.spec = {
  children: instructionSupportChildrenChecker,
  subheading: t.string,
};

export function STNEnglishLearnerSupport(props: {|
  children: InstructionSupportChildren,
  subheading: string,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    STNEnglishLearnerSupport,
  );

  if (failedValidation) return null;

  return (
    <div
      style={{
        backgroundColor: productColor.aliceBlue,
        padding: 20,
        paddingTop: 1,
        paddingBottom: 1,
        borderRadius: 8,
        marginBottom: 10,
      }}
    >
      <CollapsibleInternal
        hideHorizontalRule
        openByDefault
        title="Support for English language learners"
        icon={<EnglishLanguageLearnerIcon />}
        customChevron={CollapseChevron}
      >
        <p
          style={{
            fontWeight: 600,
            color: illustrationColor.regentGray,
            fontSize: 16,
            lineHeight: '22px',
            marginBottom: 16,
          }}
        >
          {props.subheading}
        </p>
        {props.children}
      </CollapsibleInternal>
    </div>
  );
}
STNEnglishLearnerSupport.spec = {
  children: instructionSupportChildrenChecker,
  subheading: t.string,
};

export function STNCommonMisconceptions(props: {|
  children: InstructionSupportChildren,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    STNCommonMisconceptions,
  );

  if (failedValidation) return null;

  return (
    <div
      style={{
        backgroundColor: productColor.aliceBlue,
        padding: 20,
        paddingTop: 1,
        paddingBottom: 1,
        borderRadius: 8,
        marginBottom: 10,
      }}
    >
      <CollapsibleInternal
        hideHorizontalRule
        openByDefault
        title="Common misconceptions"
        icon={<CommonMisconceptionIcon />}
        customChevron={CollapseChevron}
      >
        {props.children}
      </CollapsibleInternal>
    </div>
  );
}
STNCommonMisconceptions.spec = {
  children: instructionSupportChildrenChecker,
};
